import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, Sparkles, Sun, Flame, Heart, Coffee, Leaf, Star, Dumbbell, Brain, Battery } from "lucide-react";
import useSEO from "@/hooks/useSEO";

import WeeklyRitualStrip from "@/components/WeeklyRitualStrip";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_wellness-shots/artifacts/dibvmg73_kens%20gold.png";

const ingredients = [
  { name: "Organic Ginger", image: "https://images.unsplash.com/photo-1758055660285-d32b7b3b1e77?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", bg: "bg-honey-tint" },
  { name: "Organic Lemon", image: "https://images.unsplash.com/photo-1624436518795-a2c1c77d6896?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", bg: "bg-honey-tint" },
  { name: "Raw Honey", image: "https://images.pexels.com/photos/8851478/pexels-photo-8851478.jpeg?auto=compress&cs=tinysrgb&w=400", bg: "bg-honey-tint" },
  { name: "Organic Apple Cider Vinegar", image: "https://customer-assets.emergentagent.com/job_wellness-shots/artifacts/vy0md0kq_Home-Made-Apple-Cider-Vinegar-scaled.jpg", bg: "bg-green-tint" },
  { name: "Organic Green Tea", image: "https://images.unsplash.com/photo-1668733521099-c120aa4ebbdc?crop=entropy&cs=srgb&fm=jpg&q=85&w=400", bg: "bg-green-tint" },
];

const benefits = [
  { icon: <Zap size={24} />, title: "Natural Energy Boost", desc: "Feel awake in seconds. Ginger and Green Tea deliver clean, focused energy that lasts for hours \u2014 no crash, no jitters, just pure momentum." },
  { icon: <Shield size={24} />, title: "Immune Support", desc: "Raw honey, bright lemon, and warming ginger form a triple-shield your immune system craves. Centuries of tradition, backed by modern science." },
  { icon: <Sparkles size={24} />, title: "Gut Health & Detox", desc: "Apple Cider Vinegar resets your gut, supports healthy digestion, and helps your body flush what it doesn't need. Feel lighter every day." },
];

const stats = [
  { value: "5", label: "Power Ingredients" },
  { value: "100%", label: "Raw & Organic" },
  { value: "$5.00", label: "Per Shot, From" },
];

const useCases = [
  { id: "morning", icon: <Sun size={20} />, label: "Morning metabolism boost" },
  { id: "preworkout", icon: <Dumbbell size={20} />, label: "Pre-workout clean energy" },
  { id: "midday", icon: <Coffee size={20} />, label: "Midday reset" },
  { id: "immune", icon: <Shield size={20} />, label: "Immune support during busy weeks" },
  { id: "focus", icon: <Brain size={20} />, label: "Focus without caffeine" },
];

const whatYoullFeel = [
  { id: "ginger-kick", icon: <Flame size={18} />, text: "A warm ginger kick within 10\u201320 seconds" },
  { id: "breathing", icon: <Leaf size={18} />, text: "Clearer breathing and open airways" },
  { id: "energy", icon: <Battery size={18} />, text: "Clean, focused energy without the crash" },
  { id: "digest", icon: <Heart size={18} />, text: "A light digestive reset" },
  { id: "wakefulness", icon: <Zap size={18} />, text: "A sense of wakefulness \u2014 calm but alert" },
];

const differentiators = [
  "Raw, unheated honey",
  "Organic ginger + lemon",
  "No preservatives or fillers",
  "Made fresh in small batches",
  "Nothing artificial \u2014 ever",
];

/* ─── Sub-components ─── */

function HeroSection() {
  return (
    <section className="hero-gradient text-center py-16 md:py-24 px-6" data-testid="hero-section">
      <div className="max-w-3xl mx-auto">
        <img src={LOGO_URL} alt="Ken's Gold" className="w-48 h-48 md:w-56 md:h-56 mx-auto mb-8 object-contain" data-testid="hero-logo" />
        <div className="badge-gold mx-auto mb-6" data-testid="hero-badge">
          <Sparkles size={14} />
          100% RAW & ORGANIC
        </div>
        <h1 className="font-playfair text-4xl md:text-6xl lg:text-7xl font-bold text-brown-dark leading-tight mb-6" data-testid="hero-title">
          Nature's Power <br /><span className="text-gold italic">in Every Drop</span>
        </h1>
        <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto mb-4 leading-relaxed" data-testid="hero-description">
          Bright lemon, raw ginger heat, and a smooth honey finish — a clean burn that feels like your body turning on. One shot. Infinite benefits.
        </p>
        <p className="text-gray-400 text-sm max-w-md mx-auto mb-8 italic" data-testid="hero-social-proof">
          Loved by athletes, founders, and anyone who wants a clean daily boost without caffeine crashes.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <Link to="/shop" className="btn-gold" data-testid="hero-shop-btn">Take Your First Shot <ArrowRight size={18} /></Link>
          <Link to="/origin" className="btn-outline-gold" data-testid="hero-story-btn">Our Story</Link>
        </div>
        <div className="flex justify-center gap-6 sm:gap-12 md:gap-16" data-testid="hero-stats">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-playfair text-3xl md:text-4xl font-bold text-gold">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DifferentiatorsStrip() {
  return (
    <section className="bg-brown-dark py-5 px-6" data-testid="differentiators-strip">
      <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-x-8 gap-y-2">
        {differentiators.map((d) => (
          <span key={d} className="text-white/80 text-sm flex items-center gap-2">
            <Star size={12} className="text-gold-light" />{d}
          </span>
        ))}
      </div>
    </section>
  );
}

function UseCasesSection() {
  return (
    <section className="py-16 md:py-20 px-6" data-testid="use-cases-section">
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">When to Take It</p>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-brown-dark mb-10">Perfect For</h2>
        <div className="flex flex-wrap justify-center gap-4">
          {useCases.map((uc) => (
            <div
              key={uc.id}
              className="use-case-pill flex items-center gap-2.5 bg-honey-tint border border-gold/15 rounded-full px-6 py-3 text-sm font-medium text-brown-dark transition-all hover:bg-gold hover:text-white hover:border-gold"
              data-testid={`use-case-${uc.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {uc.icon}{uc.label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function IngredientsSection() {
  return (
    <section className="py-16 md:py-20 px-6" data-testid="ingredients-section">
      <div className="max-w-7xl mx-auto">
        <p className="text-center text-xs font-bold tracking-widest text-gold uppercase mb-3">What's Inside Ken's Gold</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mt-10">
          {ingredients.map((item) => (
            <div key={item.name} className={`${item.bg} rounded-2xl p-5 flex flex-col items-center text-center ingredient-card`} data-testid={`ingredient-${item.name.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden mb-3">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </div>
              <span className="font-dm text-sm font-medium text-brown-dark">{item.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatYoullFeelSection() {
  return (
    <section className="py-16 md:py-20 px-6 bg-cream" data-testid="what-youll-feel-section">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">After Your First Shot</p>
        <h2 className="font-playfair text-3xl md:text-5xl font-bold text-brown-dark mb-4">What You'll Feel</h2>
        <p className="text-gray-500 text-sm mb-10">This isn't a supplement you take and forget. You'll know it's working.</p>
        <div className="space-y-4 text-left max-w-md mx-auto">
          {whatYoullFeel.map((item) => (
            <div key={item.id} className="flex items-center gap-4 bg-white border border-gold/10 rounded-xl px-6 py-4 transition-all hover:border-gold/30 hover:shadow-sm" data-testid={`feel-item-${item.id}`}>
              <div className="text-gold flex-shrink-0">{item.icon}</div>
              <span className="text-brown-dark text-sm font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitsCards() {
  return (
    <section className="py-16 md:py-24 px-6" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto">
        <p className="text-center text-xs font-bold tracking-widest text-gold uppercase mb-3">Why Ken's Gold</p>
        <h2 className="font-playfair text-3xl md:text-5xl font-bold text-center text-brown-dark mb-12">Feel the Difference</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {benefits.map((b) => (
            <div key={b.title} className="benefit-card" data-testid={`benefit-${b.title.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="icon-circle mx-auto mb-5">{b.icon}</div>
              <h3 className="font-playfair text-xl font-bold text-brown-dark mb-3">{b.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/benefits" className="inline-flex items-center gap-2 text-gold font-semibold text-sm hover:gap-3 transition-all" data-testid="explore-ingredients-link">
            Explore All Ingredients <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function MicroStorySection() {
  return (
    <section className="py-16 md:py-20 px-6 bg-honey-tint" data-testid="micro-story-section">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-xs font-bold tracking-widest text-gold uppercase mb-4">The Story</p>
        <p className="font-playfair text-xl md:text-2xl text-brown-dark leading-relaxed italic">
          "Ken started crafting this blend in his own kitchen — searching for a clean, powerful daily ritual that actually worked. After hundreds of tweaks, this is the formula that stuck."
        </p>
        <Link to="/origin" className="inline-flex items-center gap-2 text-gold font-semibold text-sm mt-6 hover:gap-3 transition-all" data-testid="read-full-story-link">
          Read the full story <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

function HomeCta() {
  return (
    <section className="cta-gradient py-16 md:py-24 px-6 text-center" data-testid="cta-section">
      <div className="max-w-2xl mx-auto">
        <h2 className="font-playfair text-3xl md:text-5xl font-bold text-white italic mb-6" data-testid="cta-title">Ready to Feel Your Best?</h2>
        <p className="text-white/70 text-base md:text-lg mb-8 leading-relaxed">
          Start your wellness ritual today. Crafted with love, backed by nature, designed for you — starting at $34.99 for a weekly 7-pack.
        </p>
        <Link to="/shop" className="btn-gold text-lg" data-testid="cta-shop-btn">Take Your First Shot <ArrowRight size={18} /></Link>
      </div>
    </section>
  );
}

/* ─── Main Component ─── */

export default function Home() {
  useSEO({
    title: null,
    description: "Ken's Gold premium wellness shots crafted from Organic Ginger, Organic Lemon, Raw Honey, Organic Apple Cider Vinegar & Organic Green Tea. 100% raw, no preservatives. Weekly 7-packs from $34.99.",
  });

  return (
    <div data-testid="home-page">
      <HeroSection />
      <DifferentiatorsStrip />
      <WeeklyRitualStrip />
      <UseCasesSection />
      <IngredientsSection />
      <WhatYoullFeelSection />
      <BenefitsCards />
      <MicroStorySection />
      <HomeCta />
    </div>
  );
}
